
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, RefreshCw, Pencil } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ProviderForm } from "@/components/admin/ProviderForm";

interface Provider {
  id: string;
  name: string;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const ProviderManagement = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setProviders(data || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast({
        title: "Error fetching providers",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (provider: Provider) => {
    setEditingProvider(provider);
    setEditDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchProviders();
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setEditingProvider(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between">
          <span>Provider Management</span>
          <div className="flex gap-2">
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" /> 
                  Add Provider
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh]">
                <DialogHeader>
                  <DialogTitle>Add New Provider</DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-[calc(85vh-80px)] overflow-y-auto pr-4">
                  <div className="pt-4">
                    <ProviderForm onSuccess={handleSuccess} />
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>

            <Button size="sm" variant="outline" onClick={fetchProviders} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Manage network providers and their details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : providers.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        {provider.logo_url ? (
                          <img 
                            src={provider.logo_url} 
                            alt={`${provider.name} logo`} 
                            className="h-8 w-8 object-contain"
                          />
                        ) : (
                          <div className="h-8 w-8 bg-secondary flex items-center justify-center rounded">
                            {provider.name.charAt(0)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{provider.name}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(provider)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="max-h-[85vh]">
                <DialogHeader>
                  <DialogTitle>Edit Provider</DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-[calc(85vh-80px)] overflow-y-auto pr-4">
                  <div className="pt-4">
                    {editingProvider && (
                      <ProviderForm 
                        initialData={editingProvider} 
                        onSuccess={handleSuccess} 
                      />
                    )}
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="bg-secondary/20 rounded-lg p-4">
            <p className="text-center text-muted-foreground">
              No providers found. Add a provider to get started.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
